<template>
  <v-card>
    <v-data-table
      :model-value="selectedItems"
      @update:model-value="$emit('update:selectedItems', $event)"
      :headers="tableHeaders"
      :items="items"
      :loading="loading"
      :items-per-page="perPage"
      :row-props="getRowProps"
      :sort-by="sortBy"
      @update:sort-by="$emit('update:sortBy', $event)"
      show-select
      item-value="_uniqueId"
      return-object
      select-strategy="page"
      class="elevation-1"
    >
      <template v-slot:[`item.imsi`]="{ item }">
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="font-weight-bold clickable-cell" @click="$emit('openDetails', item.sim_id, item.imsi_id)">
              {{ item.imsi || '-' }}
            </span>
          </template>
          <div class="imsi-tooltip">
            <div><strong>IMSI:</strong> {{ item.imsi || '-' }}</div>
            <div><strong>Serial:</strong> {{ item.serial || '-' }}</div>
            <div><strong>Tip IMSI:</strong> {{ item.imsi_type_name || '-' }}</div>
            <div><strong>Retea:</strong> {{ item.network_name || '-' }}</div>
            <div><strong>Status MNO:</strong> {{ item.mno_status_name || '-' }}</div>
            <div><strong>Status SIM:</strong> {{ formatStatus(item.sim_status) }}</div>
            <div v-if="item.msisdn"><strong>MSISDN:</strong> {{ item.msisdn }}</div>
            <div v-if="item.subscriber_id"><strong>Subscriber:</strong> {{ item.subscriber_id }}</div>
            <div v-if="item.sim_id"><strong>SIM ID:</strong> {{ item.sim_id }}</div>
            <div v-if="item.sim_created_at"><strong>SIM Created:</strong> {{ formatDateTime(item.sim_created_at) }}</div>
            <div v-if="item.sim_updated_at"><strong>SIM Updated:</strong> {{ formatDateTime(item.sim_updated_at) }}</div>
          </div>
        </v-tooltip>
      </template>

      <template v-slot:[`item.serial`]="{ item }">
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="clickable-cell" @click="$emit('openDetails', item.sim_id, item.imsi_id)">
              {{ item.serial || '-' }}
            </span>
          </template>
          <div class="imsi-tooltip">
            <div><strong>IMSI:</strong> {{ item.imsi || '-' }}</div>
            <div><strong>Serial:</strong> {{ item.serial || '-' }}</div>
            <div><strong>Tip IMSI:</strong> {{ item.imsi_type_name || '-' }}</div>
            <div><strong>Retea:</strong> {{ item.network_name || '-' }}</div>
            <div><strong>Status MNO:</strong> {{ item.mno_status_name || '-' }}</div>
            <div><strong>Status SIM:</strong> {{ formatStatus(item.sim_status) }}</div>
            <div v-if="item.msisdn"><strong>MSISDN:</strong> {{ item.msisdn }}</div>
            <div v-if="item.subscriber_id"><strong>Subscriber:</strong> {{ item.subscriber_id }}</div>
            <div v-if="item.sim_id"><strong>SIM ID:</strong> {{ item.sim_id }}</div>
            <div v-if="item.sim_created_at"><strong>SIM Created:</strong> {{ formatDateTime(item.sim_created_at) }}</div>
            <div v-if="item.sim_updated_at"><strong>SIM Updated:</strong> {{ formatDateTime(item.sim_updated_at) }}</div>
          </div>
        </v-tooltip>
      </template>

      <template v-slot:[`item.sim_status`]="{ item }">
        <v-chip
          :color="getStatusColor(item.sim_status)"
          size="small"
          :class="item.sim_id ? 'clickable-link' : ''"
          @click.stop="item.sim_id ? navigateToSim(item) : null"
        >
          <v-icon v-if="item.sim_id" start size="x-small">mdi-sim</v-icon>
          {{ formatStatus(item.sim_status) }}
        </v-chip>
      </template>

      <template v-slot:[`item.msisdn`]="{ item }">
        <span v-if="item.msisdn" class="clickable-cell" @click.stop="$emit('navigateToMsisdn', item.msisdn)">
          {{ item.msisdn }}
        </span>
        <span v-else>-</span>
      </template>

      <template v-slot:[`item.expiration_date`]="{ item }">
        <span :class="isExpiringSoon(item.expiration_date) ? 'text-red' : ''">
          {{ formatDate(item.expiration_date) }}
        </span>
      </template>

      <template v-slot:[`item.services_count`]="{ item }">
        <v-chip
          v-if="item.services_count > 0 && item.sim_id"
          size="small"
          color="primary"
          class="clickable-link"
          @click.stop="$emit('navigateToServices', item.sim_id)"
        >
          <v-icon start size="small">mdi-format-list-bulleted</v-icon>
          {{ item.services_count }} {{ item.services_count === 1 ? 'serviciu' : 'servicii' }}
        </v-chip>
        <span v-else class="text-grey">-</span>
      </template>

      <template v-slot:[`item.client_name`]="{ item }">
        <span
          v-if="item.client_name && item.client_id"
          class="clickable-cell"
          @click.stop="$router.push({ name: 'clients', query: { client_id: item.client_id } })"
        >
          {{ item.client_name }}
        </span>
        <span v-else>{{ item.client_name || '-' }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex gap-1 align-center">
          <!-- Info button - always visible -->
          <v-btn
            variant="elevated"
            size="x-small"
            color="success"
            @click="$emit('openDetails', item.sim_id, item.imsi_id)"
          >
            <v-icon size="small" class="mr-1">mdi-eye</v-icon>
            Info
          </v-btn>

          <!-- Actions dropdown -->
          <v-select
            v-if="getAvailableActions(item).length > 0"
            :items="getAvailableActions(item)"
            item-title="label"
            item-value="action"
            density="compact"
            variant="outlined"
            hide-details
            placeholder="Select action:"
            class="action-select"
            @update:model-value="(val) => handleActionSelect(val, item)"
          >
            <template v-slot:selection>
              <span class="text-grey">Select action:</span>
            </template>
          </v-select>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { t as tRef } from "@/composables/useLanguages";

export default {
  name: 'ImsiDataTable',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 20
    },
    sortBy: {
      type: Array,
      default: () => []
    },
    refreshingSimId: {
      type: [Number, String],
      default: null
    },
    moveLabel: {
      type: String,
      default: 'Muta'
    }
  },
  emits: [
    'update:selectedItems',
    'update:sortBy',
    'openDetails',
    'navigateToMsisdn',
    'navigateToServices',
    'action',
    'addSubscription',
    'reprovision',
    'refresh',
    'move',
    'inventoryAdd',
    'inventoryView',
    'inventoryChange',
    'inventoryRemove',
    'navigateToSim'
  ],
  data() {
    return {
      tableHeaders: [
        { title: 'Serial', key: 'serial', sortable: true, width: '130px' },
        { title: 'IMSI', key: 'imsi', sortable: true, width: '140px' },
        { title: 'Status Retea', key: 'sim_status', sortable: true, width: '120px' },
        { title: 'Network', key: 'network_name', sortable: true, width: '100px' },
        { title: 'MSISDN', key: 'msisdn', sortable: true, width: '120px' },
        { title: 'Servicii', key: 'services_count', sortable: false, width: '100px' },
        { title: 'Client', key: 'client_name', sortable: true, width: '150px' },
        { title: 'Expiration', key: 'expiration_date', sortable: true, width: '100px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '450px' }
      ]
    }
  },
  computed: {
    t() {
      return tRef.value;
    }
  },
  methods: {
    getRowProps({ item }) {
      return {
        class: item.sim_status === 'terminated' ? 'text-grey' : ''
      }
    },
    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'not_provisioned': 'info',
        'terminated': 'grey',
        'pending_active': 'orange',
        'pending': 'orange',
        'unallocated': 'secondary'
      }
      return colors[status] || 'grey'
    },
    formatStatus(status) {
      if (!status) return '-'
      return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ro-RO')
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
    },
    isExpiringSoon(dateStr) {
      if (!dateStr) return false
      const date = new Date(dateStr)
      const now = new Date()
      const diffDays = (date - now) / (1000 * 60 * 60 * 24)
      return diffDays >= 0 && diffDays <= 30
    },
    canAddSubscription(item) {
      return item.service_id === 22 &&
             item.sim_status === 'active' &&
             item.imsi_id &&
             item.msisdn_id
    },
    canMoveImsi(item) {
      const isDistributor = item.is_distributor === true || item.is_distributor === 1 || item.is_distributor === "true"
      const isAvailable = item.is_available_for_move === true || item.is_available_for_move === 1 || item.is_available_for_move === "true"

      // Original condition: distributor with available IMSI
      if (isDistributor && isAvailable) {
        return true
      }

      // New condition: allocate_sim CS terminated - can return IMSI to parent distributor
      const isAllocateSimService = item.service_id === 22
      const csStatus = (item.client_service_status_name || item.client_service_status || '').toLowerCase()
      const isCSTerminated = csStatus === 'terminated'
      if (isAllocateSimService && isCSTerminated) {
        return true
      }

      return false
    },
    isPendingSim(item) {
      const status = item.sim_status?.toLowerCase() || ''
      return status.includes('pending') && item.sim_id
    },
    getAvailableActions(item) {
      const actions = []

      // Active: Suspend, Terminate, Add Subscription
      if (item.sim_status === 'active') {
        actions.push({ label: 'Suspend', action: 'suspend', color: 'warning' })
        actions.push({ label: 'Terminate', action: 'terminate', color: 'error' })
        if (this.canAddSubscription(item)) {
          actions.push({ label: 'Add Subscription', action: 'addSubscription', color: 'secondary' })
        }
      }

      // Suspended: Reactivate
      if (item.sim_status === 'suspended') {
        actions.push({ label: 'Reactivate', action: 'reactivate', color: 'success' })
      }

      // Not Provisioned: Provision (if not pending payment)
      if (item.sim_status === 'not_provisioned' && item.client_service_status !== 'pending_payment') {
        actions.push({ label: 'Provision', action: 'provision', color: 'success' })
      }

      // Terminated: Reprovision
      if (item.sim_status === 'terminated') {
        actions.push({ label: 'Provision', action: 'reprovision', color: 'success' })
      }

      // Pending: Refresh
      if (this.isPendingSim(item)) {
        actions.push({ label: 'Refresh Status', action: 'refresh', color: 'warning' })
      }

      // Move IMSI
      if (this.canMoveImsi(item) && !this.isPendingSim(item)) {
        actions.push({ label: this.moveLabel, action: 'move', color: 'info' })
      }

      // Inventory actions
      if (item.sim_id) {
        if (!item.in_inventory) {
          actions.push({ label: 'Add to Inventory', action: 'inventoryAdd', color: 'teal' })
        } else {
          actions.push({ label: 'View in Inventory', action: 'inventoryView', color: 'teal' })
          actions.push({ label: 'Change Inventory Status', action: 'inventoryChange', color: 'teal' })
          actions.push({ label: 'Remove from Inventory', action: 'inventoryRemove', color: 'error' })
        }
      }

      return actions
    },
    navigateToSim(item) {
      const serial = item.serial || item.imsi || ''
      if (serial) {
        this.$router.push({ name: 'sims', query: { search: serial } })
      }
    },
    handleActionSelect(action, item) {
      if (!action) return

      switch (action) {
        case 'suspend':
        case 'terminate':
        case 'reactivate':
        case 'provision':
          this.$emit('action', action, item)
          break
        case 'addSubscription':
          this.$emit('addSubscription', item)
          break
        case 'reprovision':
          this.$emit('reprovision', item)
          break
        case 'refresh':
          this.$emit('refresh', item)
          break
        case 'move':
          this.$emit('move', item)
          break
        case 'inventoryAdd':
          this.$emit('inventoryAdd', item)
          break
        case 'inventoryView':
          this.$emit('inventoryView', item)
          break
        case 'inventoryChange':
          this.$emit('inventoryChange', item)
          break
        case 'inventoryRemove':
          this.$emit('inventoryRemove', item)
          break
      }
    }
  }
}
</script>

<style scoped>
.clickable-cell {
  cursor: pointer;
}
.clickable-cell:hover {
  text-decoration: underline;
}
.clickable-link {
  cursor: pointer;
}
.imsi-tooltip {
  font-size: 0.85rem;
  line-height: 1.5;
}
.action-select {
  min-width: 160px;
  max-width: 180px;
}
.action-select :deep(.v-field__input) {
  padding: 4px 8px;
  min-height: 32px;
}
.action-select :deep(.v-select__selection-text) {
  font-size: 0.8rem;
}
</style>
